import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { zodResolver } from "@hookform/resolvers/zod";
import { Job, Organization, Proposal, SyncQuickbooksBatchInput } from "generated/graphql";
import useQuickbooksSyncBatchDraft from "hooks/jobs/useQuickbooksSyncBatchDraft";
import useGlobalMessage from "hooks/notifications/useGlobalMessage";
import { useState } from "react";
import { useForm } from "react-hook-form";
import QuickbooksEditableInformation from "../QuickbooksEditableInformation";
import QuickbooksJobPickerModal from "../QuickbooksJobPickerModal";
import QuickbooksSelectedWorkTable from "../QuickbooksSelectedWorkTable";
import QuickbooksSyncAmountSummary from "../QuickbooksSyncAmountSummary";
import QuickbooksSyncFooter from "../QuickbooksSyncFooter";
import QuickbooksSyncPreviewModal from "../QuickbooksSyncPreviewModal";
import {
  getQuickbooksSyncValidationError,
  quickbooksSyncBatchSchema,
} from "../quickbooksSyncBatchValidation";

export default function QuickbooksConfirmationModal({
  proposalId,
  job,
  organization,
  shouldPushTaxesToQuickbooks,
  mayShowTaxNotification,
  externalId,
  onClose,
}: {
  proposalId: Proposal["id"];
  job: Job;
  organization: Organization;
  shouldPushTaxesToQuickbooks?: Boolean;
  mayShowTaxNotification?: Boolean;
  externalId: string;
  onClose?: () => void;
}) {
  const draft = useQuickbooksSyncBatchDraft({
    externalId,
    job,
    mayShowTaxNotification,
    onClose,
    shouldPushTaxesToQuickbooks,
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const { show } = useGlobalMessage();
  const { handleSubmit, reset } = useForm<SyncQuickbooksBatchInput>({
    resolver: zodResolver(quickbooksSyncBatchSchema),
  });
  const handleConfirm = () => {
    const input = draft.getSyncInput();
    if (!input) {
      return;
    }

    reset(input);
    void handleSubmit(
      () => draft.submitSyncInput(input),
      (errors) => {
        show({
          message:
            getQuickbooksSyncValidationError(errors) ||
            "Please review the QuickBooks sync details.",
          options: { variant: "error", autoHideDuration: 10000 },
        });
      }
    )();
  };
  const formData = draft.formData;
  const customerName = formData?.customerName || job.company?.name || "Customer";
  const customerEmail = formData?.customerEmail || job.company?.email || "";
  const customerPhone = formData?.customerPhone || job.company?.phone || "";
  const invoiceNumber = formData?.invoiceNumber || externalId;
  const jobSiteAddress = [
    formData?.shipAddressAddressLine1 || draft.resolvedSeedJob.address?.line1,
    formData?.shipAddressAddressCity || draft.resolvedSeedJob.address?.city,
    formData?.shipAddressAddressState || draft.resolvedSeedJob.address?.state,
    formData?.shipAddressAddressZip || draft.resolvedSeedJob.address?.zip,
  ]
    .filter(Boolean)
    .join(", ");
  const billToLines = [
    formData?.customerBillingAddressLine1 || job.company?.addressLine1,
    formData?.customerBillingAddressLine2 || job.company?.addressLine2,
    [
      formData?.customerBillingAddressCity || job.company?.addressCity,
      formData?.customerBillingAddressState || job.company?.addressState,
      formData?.customerBillingAddressZip || job.company?.addressZip,
    ]
      .filter(Boolean)
      .join(", "),
    formData?.customerBillingAddressCountry || job.company?.addressCountry,
  ].filter((line): line is string => Boolean(line));
  const shipToLines = [
    formData?.shipAddressAddressLine1 || draft.resolvedSeedJob.address?.line1,
    formData?.shipAddressAddressLine2 || draft.resolvedSeedJob.address?.line2,
    [
      formData?.shipAddressAddressCity || draft.resolvedSeedJob.address?.city,
      formData?.shipAddressAddressState || draft.resolvedSeedJob.address?.state,
      formData?.shipAddressAddressZip || draft.resolvedSeedJob.address?.zip,
    ]
      .filter(Boolean)
      .join(", "),
  ].filter((line): line is string => Boolean(line));
  const taxReferenceLabel = organization.salesTaxes?.find(
    (tax) => String(tax.id) === String(formData?.taxReference || "")
  )?.name;

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "100%",
        p: { xs: 2, md: 3 },
        pb: 0,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: { xs: 0, md: 1 } }}>
        <Box sx={{ pr: 5, mb: 2 }}>
          <Typography variant="overline" color="primary.main" fontWeight={700} lineHeight={1}>
            QuickBooks Sync
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 0.25, sm: 1 }}
            alignItems={{ sm: "baseline" }}
          >
            <Typography variant="h5">Create QuickBooks Invoice</Typography>
            <Typography variant="body2" color="text.secondary">
              {invoiceNumber} for {customerName}
            </Typography>
          </Stack>
        </Box>

        <Accordion
          disableGutters
          elevation={0}
          sx={{
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px !important",
            overflow: "hidden",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 56 }}>
            <Box>
              <Typography variant="subtitle2">Invoice details</Typography>
              <Typography variant="caption" color="text.secondary">
                {customerEmail || "No billing email"}
                {jobSiteAddress ? ` · ${jobSiteAddress}` : ""}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 1, md: 1.5 }, pb: 1.5 }}>
            <QuickbooksEditableInformation
              proposalId={proposalId}
              externalId={externalId}
              job={draft.resolvedSeedJob as Job}
              organization={organization}
              hideItemsSection
              onChange={(data) => draft.setFormData(data)}
            />
          </AccordionDetails>
        </Accordion>

        <Grid container spacing={1.5} alignItems="flex-start" pb={2}>
          <Grid item xs={12} lg={9}>
            <QuickbooksSelectedWorkTable
              addManualLine={draft.addManualLine}
              allocationPreview={draft.allocationPreview}
              openJobPicker={() => draft.setJobPickerOpen(true)}
              removeLine={draft.removeLine}
              removeSelectedJob={draft.removeSelectedJob}
              seedJobId={draft.seedJobId}
              selectedJobs={draft.selectedJobs}
              updateLine={draft.updateLine}
              updateSyncAmount={draft.updateSyncAmount}
              visibleLines={draft.visibleLines}
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <QuickbooksSyncAmountSummary
              allocationPreview={draft.allocationPreview}
              billingMode={draft.billingMode}
              fixedAmountValue={draft.fixedAmountValue}
              percentageValue={draft.percentageValue}
              resetSyncAmountOverrides={draft.resetSyncAmountOverrides}
              selectedJobCount={draft.selectedJobIds.length}
              setBillingMode={draft.setBillingMode}
              setFixedAmountValue={draft.setFixedAmountValue}
              setPercentageValue={draft.setPercentageValue}
              syncAmountOverrideCount={draft.syncAmountOverrideCount}
            />
          </Grid>
        </Grid>
      </Box>

      <QuickbooksSyncFooter
        availableBillingStatuses={draft.availableBillingStatuses}
        jobBillingStatus={draft.jobBillingStatus}
        loading={draft.loading}
        onCancel={onClose}
        onConfirm={handleConfirm}
        onPreview={() => setPreviewOpen(true)}
        setJobBillingStatus={draft.setJobBillingStatus}
        setSyncEstimate={draft.setSyncEstimate}
        setSyncInvoice={draft.setSyncInvoice}
        setSyncJob={draft.setSyncJob}
        syncEstimate={draft.syncEstimate}
        syncInvoice={draft.syncInvoice}
        syncJob={draft.syncJob}
      />

      <QuickbooksJobPickerModal
        addJobsToBatch={draft.addJobsToBatch}
        closeJobPicker={draft.closeJobPicker}
        jobPickerOpen={draft.jobPickerOpen}
        jobPickerSearch={draft.jobPickerSearch}
        jobsLoading={draft.jobsLoading}
        pickerJobs={draft.pickerJobs}
        pickerSelectedJobIds={draft.pickerSelectedJobIds}
        setJobPickerSearch={draft.setJobPickerSearch}
        togglePickerJob={draft.togglePickerJob}
      />

      <QuickbooksSyncPreviewModal
        allocationPreview={draft.allocationPreview}
        billToLines={billToLines}
        customerEmail={customerEmail}
        customerName={customerName}
        customerPhone={customerPhone}
        invoiceNumber={invoiceNumber}
        jobName={formData?.jobName || draft.resolvedSeedJob.proposalStage?.name || ""}
        loading={draft.loading}
        onClose={() => setPreviewOpen(false)}
        onConfirm={handleConfirm}
        open={previewOpen}
        selectedJobs={draft.selectedJobs}
        shipToLines={shipToLines}
        syncEstimate={draft.syncEstimate}
        syncInvoice={draft.syncInvoice}
        taxReferenceLabel={taxReferenceLabel || ""}
        visibleLines={draft.visibleLines}
      />
    </Box>
  );
}
